/**
 * Pure, side-effect-free helpers shared by the comparison and merge services.
 * Kept framework-free so they are trivially unit-testable.
 */

import { DuplicateLead, LeadNote, LeadRef, MergeEntry } from '../models/duplicate-lead-merge.types';

/** Keys that are structural/internal and must never be surfaced as custom fields. */
export const INTERNAL_LEAD_KEYS = new Set<string>([
  '_id',
  '__v',
  'id',
  'office_id',
  'created_at',
  'createdAt',
  'updated_at',
  'updatedAt',
  'duplicate_of',
  'is_active',
  'is_read',
  'is_deleted',
  'deleted_at',
  'status_type',
  // derived / populated / internal fields that only add noise to the compare view
  'listing_project',
  'project_formatted',
  'project_address',
  'Project_Name',
  'agent_responsible_name',
  'report_notes_summary',
  'lead_status_log',
  'lead_source_name',
  'ofi_id',
  'ofi_ids',
  'server_created_at',
  'contact_id',
  'access_agents',
  'temperature_log',
  // CRM-sync / technical flags — internal plumbing, not lead data the user copies
  'dynamic_sync_dirty',
  'dynamics_sync_dirty',
  'dynamic_sync_status',
  'leadid',
  'lead_id',
  'dynamics_id',
  'dynamics_lead_id',
  'crm_id',
  'is_synced',
  'sync_status',
]);

/** 24-char Mongo ObjectId — an unresolved reference, never worth showing raw. */
export function isObjectIdLike(value: any): boolean {
  return typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value.trim());
}

/** ISO / machine timestamp string — noise as a "custom field". */
export function isIsoDateLike(value: any): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value.trim());
}

/** Key patterns that signal an internal/derived field (ids, timestamps, sync flags, logs…). */
const NOISE_KEY_RE =
  /(^_)|(_id$)|(_ids$)|(_at$)|(_ts$)|(_log$)|(_slug$)|(_html$)|(token)|(password)|(__)|(dirty)|(sync)|(^dynamic)|(^leadid$)|(_flag$)|(_status_id$)/i;

/** Keys the server sets/owns — never echo these back in an update body. */
export const SERVER_MANAGED_KEYS = ['__v', 'createdAt', 'updatedAt'];

/**
 * Keys the `/update-lead/:id` validator rejects when EMPTY (422 "… required").
 * A raw lead loaded from Mongo can omit any field that was never set, so before
 * updating we back-fill these from a sibling lead that does have them.
 */
export const REQUIRED_UPDATE_KEYS = ['enquiry_type', 'lead_source', 'lead_status'];

/**
 * Status written onto the duplicates when they are closed. `lead_status` is bound by `tag_name`
 * (a plain string, see the Lead Status select in edit-lead-detail), and the office's status list
 * uses this `Disqualified: …` convention already — the same shape as `Disqualified: Spam`, which
 * the block-lead feature counts across projects.
 */
export const DUPLICATE_LEAD_STATUS = 'Disqualified: Duplicate';

/**
 * First non-blank value for each required key across a set of leads. The
 * duplicates are the same person, so a sibling's value is a valid stand-in for
 * any lead whose required field is empty.
 */
export function requiredDefaultsFrom(leads: DuplicateLead[]): Record<string, any> {
  const defaults: Record<string, any> = {};
  for (const key of REQUIRED_UPDATE_KEYS) {
    const found = leads.find(l => !isBlank(l?.[key]));
    if (found) defaults[key] = found[key];
  }
  return defaults;
}

/**
 * Build a full `/update-lead/:id` body from a lead + overrides — the same shape
 * the edit-lead form saves: whole record, server keys stripped, required-but-empty
 * fields back-filled from `requiredDefaults`. Overrides always win.
 */
export function toLeadUpdateBody(
  lead: DuplicateLead,
  overrides: Record<string, any> = {},
  requiredDefaults: Record<string, any> = {}
): Record<string, any> {
  const body: Record<string, any> = { ...lead, ...overrides };
  for (const key of SERVER_MANAGED_KEYS) delete body[key];
  for (const key of REQUIRED_UPDATE_KEYS) {
    if (isBlank(body[key])) body[key] = requiredDefaults[key] ?? body[key] ?? null;
  }
  return body;
}

export function isBlank(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Blank for comparison purposes. A `false` flag (Inspection Booked, Application Submitted…) only
 * means "never set", so it reads the same as a missing value — the client doesn't want a False vs
 * empty row offered as a difference.
 */
export function isEmptyValue(value: any): boolean {
  if (value === false) return true;
  if (typeof value === 'string' && /^false$/i.test(value.trim())) return true;
  return isBlank(value);
}

/**
 * Best-effort human rendering of an arbitrary lead value. Handles the common
 * shapes seen in this codebase: raw scalars, populated ref objects
 * ({ name } / { tag_name } / { full_name } …) and arrays thereof.
 */
export function displayValue(value: any): string {
  if (value === null || value === undefined) return '';
  // Booleans read as words in the UI, not as raw literals — and the API sends
  // some of these flags as the strings 'true'/'false', so normalise both.
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim();
    if (/^true$/i.test(text)) return 'True';
    if (/^false$/i.test(text)) return 'False';
    return text;
  }
  if (Array.isArray(value)) {
    return value.map(displayValue).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    return String(value.full_name ?? value.name ?? value.tag_name ?? value.label ?? value.title ?? value.value ?? value.email ?? '').trim();
  }
  return '';
}

/** Identity of a value for equality: the populated ref id when present, else its display. */
export function valueIdentity(value: any): string {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const id = value._id ?? value.id;
    if (id) return `id:${id}`;
  }
  return `v:${displayValue(value).toLowerCase()}`;
}

export function valuesEqual(a: any, b: any): boolean {
  return valueIdentity(a) === valueIdentity(b);
}

/** Full name of a lead, falling back through the shapes the API returns. */
export function leadName(lead: Partial<DuplicateLead>): string {
  const full = (lead.full_name ?? '').toString().trim();
  if (full) return full;
  const composed = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return composed || (lead.email ?? '').toString().trim() || 'Unknown lead';
}

export function toLeadRef(lead: Partial<DuplicateLead> & { _id: string }): LeadRef {
  return { _id: lead._id, name: leadName(lead), email: lead.email };
}

/** Human name of a lead's project, resolving the populated ref / sibling name keys. */
export function projectName(lead: Partial<DuplicateLead>): string {
  const lp: any = lead.listing_project;
  const candidate =
    lp?.Project_Name ??
    lp?.project ??
    lp?.name ??
    (lead as any).Project_Name ??
    (lead as any).project_formatted ??
    lead.project_address ??
    lead.listing ??
    lead.property ??
    lead.project;
  const text = displayValue(candidate);
  return isObjectIdLike(text) ? '' : text;
}

/** Human name of a lead's assigned agent (prefers the *_name sibling over the id). */
export function agentName(lead: Partial<DuplicateLead>): string {
  const candidate = (lead as any).agent_responsible_name ?? (lead as any).agent_responsible_formatted ?? lead.agent_responsible;
  const text = displayValue(candidate);
  return isObjectIdLike(text) ? '' : text;
}

/** Owner's display name; `Owner` itself is usually a raw user/team id. */
export function ownerName(lead: Partial<DuplicateLead>): string {
  const text = displayValue((lead as any).Owner_formatted ?? (lead as any).Owner);
  return isObjectIdLike(text) ? '' : text;
}

export function noteText(note: LeadNote | string): string {
  if (typeof note === 'string') return note.trim();
  return (note.note ?? note.text ?? note.description ?? '').toString().trim();
}

export function pickDate(...candidates: any[]): string | undefined {
  for (const c of candidates) {
    if (c) return typeof c === 'string' ? c : String(c);
  }
  return undefined;
}

export function pickCreatedBy(source: any): string | undefined {
  if (!source) return undefined;
  if (typeof source === 'string') return source;
  return source.full_name ?? source.name ?? source.email ?? source._id ?? undefined;
}

/**
 * De-duplicate merge entries, preserving order. `seen` may be pre-seeded with
 * keys already present on the kept lead so its own entries are excluded.
 */
export function dedupeEntries(entries: MergeEntry[], keyOf: (e: MergeEntry) => string, seen = new Set<string>()): MergeEntry[] {
  const out: MergeEntry[] = [];
  for (const entry of entries) {
    const key = keyOf(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}

/** Discover custom-field keys present on any candidate but not already modelled. */
export function discoverCustomKeys(leads: DuplicateLead[], knownKeys: Set<string>): string[] {
  const found = new Set<string>();
  for (const lead of leads) {
    for (const key of Object.keys(lead ?? {})) {
      if (knownKeys.has(key) || INTERNAL_LEAD_KEYS.has(key)) continue;
      if (NOISE_KEY_RE.test(key)) continue;
      const v = lead[key];
      if (typeof v === 'function') continue;
      // Skip objects/arrays (nested refs/collections) and machine-only values.
      if (v && typeof v === 'object') continue;
      if (isObjectIdLike(v) || isIsoDateLike(v)) continue;
      found.add(key);
    }
  }
  return Array.from(found).sort();
}

/** Convert a snake/camel key into a Title Case label for custom fields. */
export function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}
